import Header from "src/components/header/Header.component";
import { Colors } from "src/constants/colors.constants";
import { ChevronLeft } from "@mui/icons-material";
import {
  Button,
  Card,
  Input,
  Stack,
  Switch,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { UserRole } from "src/store/users";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  blockAccount,
  getAccountHolders,
  setSuperAdminToHolder,
  updateAccountHolder,
} from "src/store/accountHolders/accountHolders.services";
import { useEffect, useMemo, useState } from "react";
import { AccountHolders } from "src/store/accountHolders/accountHolders.models";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { enqueueSnackbar } from "notistack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

type SortField = "name" | "isActive";
type SortDirection = "asc" | "desc";

const COMPANY_COLUMN_FLEX = 1.4;

const AccountHolderScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [accountHolders, setAccountHolders] = useState<AccountHolders[]>();
  const [isEditAccountHolder, setIsEditAccountHolder] = useState<string>();
  const [companyNames, setCompanyNames] = useState<{ [key: string]: string }>(
    {},
  );
  const [sortConfig, setSortConfig] = useState<{
    field: SortField;
    direction: SortDirection;
  }>({
    field: "isActive",
    direction: "asc",
  });

  const fetchAccountHolders = async () => {
    const x = await getAccountHolders();
    if (x) setAccountHolders(x);
  };

  useEffect(() => {
    fetchAccountHolders();
  }, []);

  const handleRowClick = (id: string) => {
    setSuperAdminToHolder(id).then((x) => {
      navigate(`/data/payments`);
      window.location.reload();
    });
  };

  const handleBlockAccount = async (accountHolderId: string) => {
    try {
      await blockAccount(accountHolderId);
      setAccountHolders((prev) =>
        prev?.map((accountHolder) =>
          accountHolder.accountHolderId === accountHolderId
            ? { ...accountHolder, isActive: !accountHolder.isActive }
            : accountHolder,
        ),
      );
    } catch (err) {
      enqueueSnackbar("Une erreur est survenue, veuillez réessayer");
    }
  };

  const handleEditAccountHolder = (accountHolderId: string, name: string) => {
    setIsEditAccountHolder(accountHolderId);
    setCompanyNames((prev) => ({ ...prev, [accountHolderId]: name }));
  };

  const handleSaveAccountHolder = async (
    companyId: string,
    accountHolderId: string,
  ) => {
    const name = companyNames[accountHolderId];
    updateAccountHolder(companyId, name).then(() => {
      setIsEditAccountHolder(undefined);
      fetchAccountHolders();
    });
  };

  const handleSort = (field: SortField) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedAccountHolders = useMemo(() => {
    const compareCompanyNameAsc = (a: AccountHolders, b: AccountHolders) =>
      (a.name ?? "").localeCompare(b.name ?? "", undefined, {
        sensitivity: "base",
      });

    return accountHolders?.slice().sort((a, b) => {
      const directionMultiplier = sortConfig.direction === "asc" ? 1 : -1;

      if (sortConfig.field === "isActive") {
        const statusA = a.isActive
          ? t("clientAccounts.table.active")
          : t("clientAccounts.table.inactive");
        const statusB = b.isActive
          ? t("clientAccounts.table.active")
          : t("clientAccounts.table.inactive");
        const statusComparison =
          statusA.localeCompare(statusB, undefined, { sensitivity: "base" }) *
          directionMultiplier;

        return statusComparison || compareCompanyNameAsc(a, b);
      }

      return compareCompanyNameAsc(a, b) * directionMultiplier;
    });
  }, [accountHolders, sortConfig.direction, sortConfig.field, t]);

  return (
    <Stack alignItems="start" height="100%" gap={2} pt={3}>
      <Stack width="100%">
        <Header role={UserRole.SUPER_ADMIN} />
      </Stack>
      <Button
        variant="text"
        startIcon={<ChevronLeft />}
        onClick={() => navigate(-1)}
        aria-label="back"
      >
        {t("global.back")}
      </Button>
      <Stack
        direction="row"
        justifyContent="space-between"
        width="100%"
        flexWrap="wrap"
      >
        <Typography fontSize="30px" fontWeight={800} color={Colors.primaryText}>
          {t("clientAccounts.title")}
        </Typography>
      </Stack>
      {accountHolders && (
        <Stack
          direction="row"
          width="100%"
          p="10px"
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography flex={2} color={Colors.secondaryText}>
            {t("clientAccounts.table.clientEmail")}
          </Typography>
          <TableSortLabel
            active={sortConfig.field === "name"}
            direction={
              sortConfig.field === "name" ? sortConfig.direction : "asc"
            }
            onClick={() => handleSort("name")}
            sx={{ flex: COMPANY_COLUMN_FLEX, pr: 0 }}
          >
            <Typography color={Colors.secondaryText}>
              {t("clientAccounts.table.companyName")}
            </Typography>
          </TableSortLabel>
          <TableSortLabel
            active={sortConfig.field === "isActive"}
            direction={
              sortConfig.field === "isActive" ? sortConfig.direction : "asc"
            }
            onClick={() => handleSort("isActive")}
            sx={{ flex: 0.8 }}
          >
            <Typography color={Colors.secondaryText}>
              {t("clientAccounts.table.status")}
            </Typography>
          </TableSortLabel>
          <Typography flex={1} textAlign="right" color={Colors.secondaryText}>
            {t("clientAccounts.table.actions")}
          </Typography>
        </Stack>
      )}
      {sortedAccountHolders ? (
        sortedAccountHolders.map((accountHolder) => (
          <Stack
            component={Card}
            width="100%"
            p="20px"
            justifyContent={"space-between"}
            alignItems={"center"}
            key={accountHolder.accountHolderId}
            direction="row"
            sx={{
              ":hover": {
                boxShadow: 10,
              },
            }}
          >
            <Typography
              flex={2}
              sx={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {accountHolder.email}
            </Typography>
            <Stack
              flex={COMPANY_COLUMN_FLEX}
              minWidth={0}
              direction="row"
              alignItems={"center"}
              spacing={1}
              pr={2}
            >
              <Stack flex={0.7} minWidth={0}>
                {isEditAccountHolder === accountHolder.accountHolderId ? (
                  <Input
                    placeholder="Company name"
                    onChange={(e) =>
                      setCompanyNames((prev) => ({
                        ...prev,
                        [accountHolder.accountHolderId]: e.target.value,
                      }))
                    }
                    value={companyNames[accountHolder.accountHolderId] || ""}
                    defaultValue={accountHolder.name}
                    fullWidth
                  />
                ) : (
                  <Typography sx={{ overflowWrap: "anywhere" }}>
                    {accountHolder.name ?? "Aucun nom de société"}
                  </Typography>
                )}
              </Stack>
              <Stack flex="0 0 auto" justifyContent={"flex-end"}>
                {isEditAccountHolder === accountHolder.accountHolderId ? (
                  <SaveOutlinedIcon
                    onClick={() =>
                      handleSaveAccountHolder(
                        accountHolder.companyId,
                        accountHolder.accountHolderId,
                      )
                    }
                    style={{ cursor: "pointer" }}
                  />
                ) : (
                  <EditOutlinedIcon
                    onClick={() =>
                      handleEditAccountHolder(
                        accountHolder.accountHolderId,
                        accountHolder.name,
                      )
                    }
                    style={{ cursor: "pointer" }}
                  />
                )}
              </Stack>
            </Stack>

            <Typography
              flex={0.8}
              color={
                accountHolder.isActive
                  ? Colors.primaryText
                  : Colors.secondaryText
              }
            >
              {accountHolder.isActive
                ? t("clientAccounts.table.active")
                : t("clientAccounts.table.inactive")}
            </Typography>

            <Stack
              flex={1}
              direction="row"
              justifyContent={"flex-end"}
              alignItems={"center"}
              spacing={2}
            >
              <Tooltip title={t("clientAccounts.table.seeAccount")}>
                <Stack
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    handleRowClick(accountHolder.accountHolderId);
                  }}
                >
                  <VisibilityOutlinedIcon color="primary" />
                </Stack>
              </Tooltip>
              <Tooltip title={t("clientAccounts.table.blockAccount")}>
                <Switch
                  defaultChecked={accountHolder.isActive}
                  onChange={() =>
                    handleBlockAccount(accountHolder.accountHolderId)
                  }
                />
              </Tooltip>
            </Stack>
          </Stack>
        ))
      ) : (
        <Typography> {t("clientAccounts.noAccounts")}</Typography>
      )}
    </Stack>
  );
};

export default AccountHolderScreen;
